/**
 * Categorization helpers for store copy actions, used by the action filter tabs.
 * Tab counts come from the attempt's per-action-type counters; filtering happens server-side
 * by sending a category's action types. "Requires attention" surfaces the integration_not_found
 * action (the main failure mode).
 */
import { StoreCopyActionCounts, StoreCopyActionType } from '../../../../services/api/troubleshooting-service';

export type ActionCategory =
    | 'requires_attention'
    | 'all'
    | 'created'
    | 'updated'
    | 'integration'
    | 'stale';

export interface IActionTab {
    id: ActionCategory;
    label: string;
    count: number;
}

/** Action types each category covers; null means every action type. */
export const CATEGORY_ACTIONS: Record<ActionCategory, StoreCopyActionType[] | null> = {
    requires_attention: ['integration_not_found'],
    all: null,
    created: ['created'],
    updated: ['updated'],
    integration: ['integration_linked', 'integration_not_found'],
    stale: ['disabled', 'soft_deleted'],
};

const CATEGORY_LABELS: Record<ActionCategory, string> = {
    requires_attention: 'Requires attention',
    all: 'All',
    created: 'Created',
    updated: 'Updated',
    integration: 'Integrations',
    stale: 'Stale cleanup',
};

export const countCategory = (counts: Partial<StoreCopyActionCounts>, category: ActionCategory): number => {
    const actions = CATEGORY_ACTIONS[category];
    const values = actions === null
        ? Object.values(counts)
        : actions.map((action) => counts[action]);
    return values.reduce<number>((sum, value) => sum + (value ?? 0), 0);
};

export const requiresAttentionCount = (counts: Partial<StoreCopyActionCounts>): number =>
    countCategory(counts, 'requires_attention');

/**
 * Builds the tab list from an attempt's action counters. "Requires attention" and "All" always
 * appear; the remaining category tabs appear only when they contain at least one action.
 */
export const buildActionTabs = (counts: Partial<StoreCopyActionCounts>): IActionTab[] => {
    const order: ActionCategory[] = ['requires_attention', 'all', 'created', 'updated', 'integration', 'stale'];
    const alwaysShown: ActionCategory[] = ['requires_attention', 'all'];

    return order
        .map((id) => ({ id, label: CATEGORY_LABELS[id], count: countCategory(counts, id) }))
        .filter((tab) => alwaysShown.includes(tab.id) || tab.count > 0);
};
